// Фіскальний касовий чек ПРРО для друку й екрана — реквізити й порядок за Положенням про форму та зміст розрахункових документів
// (наказ Мінфіну від 21.01.2016 № 13, ред. від 11.01.2025): розділ II — чек на товари (послуги), розділ III — видатковий чек (повернення).
// Номери в коментарях — рядки п. 2 відповідного розділу; вигляд — за бланками ФКЧ-1 і ФКЧ-2 (додатки 1–2, docs/dps-prro/forma). Не друкуємо: 12–17 (дані платіжного термінала — терміналом eCabinet не
// керує, термінал друкує свій чек), 22 (акциз), 28 (безмитна торгівля), 30 і 33 (лише для РРО), 32 (лише офлайн).
// QR (рядок 29) — запит до СОД РРО за форматом опису API ДПС «Перевірка чеку» (для онлайн-чека без MAC).
const money = (k) => `${k < 0 ? "-" : ""}${Math.floor(Math.abs(k) / 100)}.${String(Math.abs(k) % 100).padStart(2, "0")}`;
const qtyText = (m) => (m / 1000).toFixed(3).replace(/\.?0+$/, "");
const PAY = { cash: "ГОТІВКА", card: "БЕЗГОТІВКОВА" };
const MEANS = { card: "Електронний платіжний засіб" }; // рядок 19 ФКЧ-1 — засіб оплати для безготівкової форми
const UAH = "ГРН";

// ts — YYYYMMDDhhmmss; doc — { kind: sale|return, check (computeCheck), fiscalId, ts }; taxes — групи каси [{ tx, letter, rate }]
// → { title, lines: [{ text } | { left, right } | { qr }, з b — жирним, c — по центру], qr } — рядки вже в порядку бланка,
// QR (рядок 29) — на своєму місці, останній рядок — напис «ФІСКАЛЬНИЙ ЧЕК»/«ВИДАТКОВИЙ ЧЕК» і виробник (рядок 35/31)
// test — документ тестового сервера ДПС: не фіскальний, і це видно на самому чеку (перший і останній рядки)
export function receipt({ seller, point, fn, doc, taxes, online = true, test = false }) {
  const ret = doc.kind === "return", c = doc.check;
  const letter = (tx) => taxes.find((t) => String(t.tx) === String(tx))?.letter || "";
  const L = [];
  if (test) L.push({ text: "ТЕСТОВИЙ ДОКУМЕНТ — НЕ ФІСКАЛЬНИЙ", c: true, b: true });
  // 1–5: продавець, господарська одиниця, адреса, ІПН платника ПДВ («ПН»)
  L.push({ text: seller.name, c: true, b: true });
  if (point?.name) L.push({ text: point.name, c: true });
  if (point?.address) L.push({ text: point.address, c: true });
  if (seller.ipn) L.push({ text: `ПН ${seller.ipn}`, c: true });
  // 6–11: для кожної позиції — кількість і ціна (якщо не одна одиниця), штрихкод, назва, вартість і літера ставки
  for (const l of c.lines) {
    if (l.qtyMilli !== 1000) L.push({ text: `${qtyText(l.qtyMilli)} х ${money(l.priceKop)}` }); // знак «х» (кирилиця, як у бланку)
    if (l.barcode) L.push({ text: l.barcode });
    L.push({ left: l.name, right: `${money(l.sumKop)} ${letter(l.tx)}`.trim() });
  }
  // 18 (ФКЧ-2 — 18 «форма виплати»): форма, сума коштів за нею (готівка — після заокруглення) і валюта; 19 (лише ФКЧ-1) — засіб оплати
  for (const p of c.payments) {
    L.push({ left: PAY[p.type], right: `${money(p.sum)} ${UAH}` });
    if (!ret && MEANS[p.type]) L.push({ text: MEANS[p.type] });
  }
  // 20–21: загальна сума; ПДВ за кожною ставкою (нульові можна не друкувати)
  L.push({ left: "СУМА", right: money(c.total), b: true });
  for (const g of c.groups) if (g.vat) L.push({ left: `ПДВ ${letter(g.tx)} ${g.rate}%`, right: money(g.vat) });
  // 23–25 (лише ФКЧ-1; у видатковому чеку цих рядків немає): заокруглення, до сплати й решта з валютою (п. 9: «Заокруглення»
  // після ПДВ, далі «До сплати»)
  const cash = !ret && c.payments.find((p) => p.type === "cash");
  if (cash?.down || cash?.up) {
    L.push({ left: "Заокруглення", right: money(cash.up ? cash.up : -cash.down) });
    L.push({ left: "До сплати", right: `${money(c.total - (cash.down || 0) + (cash.up || 0))} ${UAH}`, b: true });
  }
  if (cash?.change) L.push({ left: "Решта", right: `${money(cash.change)} ${UAH}` });
  // 26–27: фіскальний номер чека, дата й час
  // Дата — «дд.мм.рррр», час — «гг-хх-сс», як у бланку (рядок 27); у QR — формат опису API ДПС
  const t = doc.ts, date = `${t.slice(6, 8)}.${t.slice(4, 6)}.${t.slice(0, 4)}`, time = `${t.slice(8, 10)}:${t.slice(10, 12)}:${t.slice(12, 14)}`;
  L.push({ left: "ЧЕК №", right: doc.fiscalId || "—" });
  L.push({ left: date, right: time.replace(/:/g, "-") });
  const qr = doc.fiscalId ? `https://cabinet.tax.gov.ua/cashregs/check?date=${t.slice(0, 8)}&time=${time}&fn=${encodeURIComponent(fn)}&id=${encodeURIComponent(doc.fiscalId)}&sm=${money(c.total)}` : null;
  if (qr) L.push({ qr });
  // 31, 34, 35: режим, ФН ПРРО, напис і назва виробника — одним рядком
  const title = test ? `ТЕСТОВИЙ ${ret ? "ВИДАТКОВИЙ " : ""}ЧЕК (НЕФІСКАЛЬНИЙ)` : ret ? "ВИДАТКОВИЙ ЧЕК" : "ФІСКАЛЬНИЙ ЧЕК";
  L.push({ text: online ? "ОНЛАЙН" : "ОФЛАЙН", c: true });
  L.push({ left: "ФН ПРРО", right: fn });
  L.push({ left: title, right: "eCabinet", b: true });
  return { title, lines: L, qr };
}
